import { Dispatch } from 'redux';

import escape from 'escape-html';
import { IUserAttr } from '../actions/session';
import * as Actions from '../actions/testForm';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import { IOptions } from '../utils/Transfers';
import AbstractScenario from './AbstractScenario';

/** eラーニングテスト情報取得シナリオ用パラメータ */
export interface IFetchTestParam {
	careerID: string;
	classID: string;
	testID: string;
	userAttr: IUserAttr;
	isPreview: boolean;
}

/** eラーニングテスト提出シナリオ用パラメータ */
export interface IMarkTestParms {
	careerID: string;
	classID: string;
	testID: string;
	thistoryID: number;
	startTime: string;
	examStartTime: string;
	userAttr: IUserAttr;
	groups: Actions.IGroup[];
	answers: { [questionID: string]: string[] };
	signedQuestionIDs: Set<string>;
	isPreview: boolean;

	dbms: string | null;
	cnt: number;
	questionGroupDisplay: boolean;
	groupCount: number;
	markAfter: boolean;
	testSetQuizCount: number;
	timeLimit: number;
	retries: number;
}

/**
 * テストシナリオファクトリ
 */
export default class TestFormScenarioFactory {
	/**
	 * eラーニング全問一画面テスト情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchTestCollect(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<IFetchTestParam> {
			protected process(arg: IFetchTestParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerCount: 0,
					careerID: arg.careerID,
					classID: arg.classID,
					dummy: '',
					fileNum: '',
					groupInfoList: [],
					lang: arg.userAttr.lang,
					questionID: '',
					quizCount: 0,
					testID: arg.testID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read('el_test_TestFormBlockModel', param).then(
					data => {
						TestFormScenarioFactory.dispatchTestFormParams(
							this.dispatch,
							data
						);
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング一問一画面テスト情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchTestStep(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<IFetchTestParam> {
			protected process(arg: IFetchTestParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerCount: 0,
					careerID: arg.careerID,
					classID: arg.classID,
					dummy: '',
					lang: arg.userAttr.lang,
					quizCount: 0,
					testID: arg.testID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read('el_test_TestStepFormBlockModel', param).then(
					data => {
						TestFormScenarioFactory.dispatchTestFormParams(
							this.dispatch,
							data
						);
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングテスト提出シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createMarkTest(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<IMarkTestParms> {
			protected process(arg: IMarkTestParms) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerCount: 0,
					careerID: arg.careerID,
					classID: arg.classID,
					cnt: arg.cnt,
					dbms: arg.dbms,
					displayTmpSaveBtn: false,
					dummy: '',
					examStartTime: arg.examStartTime,
					fileNum: '',
					groupCount: arg.groupCount,
					groupInfoList: arg.groups.map(g =>
						TestFormScenarioFactory.makeGroupParam(
							g,
							arg.answers,
							arg.signedQuestionIDs
						)
					),
					lang: arg.userAttr.lang,
					markAfter: arg.markAfter,
					questionGroupDisplay: arg.questionGroupDisplay,
					questionID: '',
					quizCount: 0,
					retries: arg.retries,
					saveType: 1,
					startTime: arg.startTime,
					testID: arg.testID,
					testSetQuizCount: arg.testSetQuizCount,
					thistoryID: arg.thistoryID,
					timeLimit: arg.timeLimit,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_test_TestFormBlockModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングテスト提出シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createMarkTestStep(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<IMarkTestParms> {
			protected process(arg: IMarkTestParms) {
				const param = {
					accessType: 10,
					answerCount: 0,
					careerID: arg.careerID,
					classID: arg.classID,
					cnt: arg.cnt,
					currentQuestion: null,
					dbms: arg.dbms,
					displayTmpSaveBtn: false,
					dummy: '',
					examStartTime: arg.examStartTime,
					groupCount: arg.groupCount,
					groupInfoList: arg.groups.map(g =>
						TestFormScenarioFactory.makeGroupParam(
							g,
							arg.answers,
							arg.signedQuestionIDs
						)
					),
					lang: arg.userAttr.lang,
					markAfter: arg.markAfter,
					questionGroupDisplay: arg.questionGroupDisplay,
					quizCount: 0,
					retries: arg.retries,
					saveType: 1,
					startTime: arg.startTime,
					testID: arg.testID,
					testSetQuizCount: arg.testSetQuizCount,
					thistoryID: arg.thistoryID,
					timeLimit: arg.timeLimit,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_test_TestStepFormBlockModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングテスト一時保存シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createSaveTest(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<IMarkTestParms> {
			protected process(arg: IMarkTestParms) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerCount: 0,
					careerID: arg.careerID,
					classID: arg.classID,
					examStartTime: arg.examStartTime,
					groupInfoList: arg.groups.map(g =>
						TestFormScenarioFactory.makeGroupParam(
							g,
							arg.answers,
							arg.signedQuestionIDs
						)
					),
					lang: arg.userAttr.lang,
					markAfter: arg.markAfter,
					questionID: '',
					quizCount: 0,
					saveType: 0,
					startTime: arg.startTime,
					testID: arg.testID,
					thistoryID: arg.thistoryID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_test_TestFormBlockModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	protected static dispatchTestFormParams(dispatch: Dispatch, data: any) {
		const timeLimit: number = data.timeLimit;
		const retries: number = data.retries;
		const testSetQuizCount: number = data.testSetQuizCount;
		const startTime: string = data.startTime;
		const examStartTime: string = data.examStartTime;
		const thistoryID: number = data.thistoryID;
		const displayTmpSaveBtn: boolean = data.displayTmpSaveBtn;
		const markAfter: boolean = data.markAfter;
		const groups = data.groupInfoList.map((g: any) => this.toGroup(g));
		const dbms: string | null = data.dbms;
		const cnt: number = data.cnt;
		const groupCount: number = data.groupCount;
		const questionGroupDisplay = data.questionGroupDisplay;
		dispatch(
			Actions.setTestFormParams({
				displayTmpSaveBtn,
				examStartTime,
				groups,
				markAfter,
				questionGroupDisplay,
				retries,
				startTime,
				testSetQuizCount,
				thistoryID,
				timeLimit,

				cnt,
				dbms,
				groupCount,
			})
		);
	}

	protected static toGroup(receivedGroup: any) {
		const group: Actions.IGroup = {
			groupTitle: receivedGroup.groupTitle,
			questions: receivedGroup.questionInfoList.map((q: any) =>
				this.toQuestion(q)
			),
		};
		return group;
	}

	protected static toQuestion(receivedQuestion: any) {
		let questionKind: Actions.QuestionKind = Actions.QuestionKind.RADIO;
		let answers: Actions.IAnswers = [];
		if (receivedQuestion.questionKind === 'radio') {
			questionKind = Actions.QuestionKind.RADIO;
			answers = receivedQuestion.radioAnswerInfoList.map((a: any) =>
				this.toRadioAnswer(a)
			);
		} else if (receivedQuestion.questionKind === 'text') {
			questionKind = Actions.QuestionKind.TEXT;
			answers = receivedQuestion.textAnswerInfoList.map((a: any) =>
				this.toTextAnswer(a)
			);
		} else if (receivedQuestion.questionKind === 'check') {
			questionKind = Actions.QuestionKind.CHECK;
			answers = receivedQuestion.checkAnswerInfoList.map((a: any) =>
				this.toCheckAnswer(a)
			);
		} else if (receivedQuestion.questionKind === 'select') {
			questionKind = Actions.QuestionKind.SELECT;
			answers = receivedQuestion.selectAnswerInfoList.map((a: any) =>
				this.toSelectAnswer(a)
			);
		}
		const references: Actions.IReference[] = receivedQuestion.referenceInfoList.map(
			(r: any) => this.toReference(r)
		);
		const question: Actions.IQuestion = {
			answers,
			checked: receivedQuestion.checked,
			imageFilePath: receivedQuestion.imageFilePath,
			questionID: receivedQuestion.questionID,
			questionKind,
			questionNo: receivedQuestion.questionNo,
			questionScore: receivedQuestion.questionScore,
			questionText: ReplaceStyleKeywords.replaceStyleKeywords(
				escape(receivedQuestion.questionText)
			),
			questionTextNum: receivedQuestion.questionTextNum,
			references,
		};
		return question;
	}

	protected static toReference(receivedReference: any) {
		const answer: Actions.IReference = {
			displayFileName: receivedReference.displayFileName,
			fileNum: receivedReference.fileNum,
		};
		return answer;
	}

	protected static toRadioAnswer(receivedAnswer: any) {
		const answer: Actions.IRadioAnswer = {
			displayNum: receivedAnswer.displayNum,
			options: receivedAnswer.answersList.map((o: any) =>
				this.toRadioOption(o)
			),
			selectedAnswerID: receivedAnswer.selectedAnswerID,
		};
		return answer;
	}

	protected static toRadioOption(receivedOption: any) {
		const option: Actions.IRadioOption = {
			answerID: receivedOption.answerID,
			answerText: receivedOption.answerText,
		};
		return option;
	}

	protected static toTextAnswer(receivedAnswer: any) {
		const answer: Actions.ITextAnswer = {
			answerID: receivedAnswer.answerID,
			answerText: receivedAnswer.answerText,
			displayNum: receivedAnswer.displayNum,
		};
		return answer;
	}

	protected static toCheckAnswer(receivedAnswer: any) {
		const answer: Actions.ICheckAnswer = {
			answerID: receivedAnswer.answerID,
			answerText: receivedAnswer.answerText,
			selected: receivedAnswer.selected,
		};
		return answer;
	}

	protected static toSelectAnswer(receivedAnswer: any) {
		const answer: Actions.ISelectAnswer = {
			displayNum: receivedAnswer.displayNum,
			options: receivedAnswer.answersList.map((o: any) =>
				this.toSelectOption(o)
			),
			selectedAnswerID: receivedAnswer.selectedAnswerID,
		};
		return answer;
	}

	protected static toSelectOption(receivedOption: any) {
		const option: Actions.ISelectOption = {
			answerID: receivedOption.answerID,
			answerText: receivedOption.answerText,
		};
		return option;
	}

	protected static makeGroupParam(
		group: Actions.IGroup,
		answers: { [questionID: string]: string[] },
		signedQuestionIDs: Set<string>
	) {
		return {
			questionInfoList: group.questions.map(q =>
				this.makeQuestionParam(q, answers, signedQuestionIDs)
			),
		};
	}

	protected static makeQuestionParam(
		question: Actions.IQuestion,
		answers: { [questionID: string]: string[] },
		signedQuestionIDs: Set<string>
	) {
		const param: IOptions = {
			checkAnswerInfoList: [],
			checked: signedQuestionIDs.has(question.questionID),
			questionID: question.questionID,
			radioAnswerInfoList: [],
			selectAnswerInfoList: [],
			textAnswerInfoList: [],
		};
		switch (question.questionKind) {
			case Actions.QuestionKind.RADIO:
				param.questionKind = 'radio';
				param.radioAnswerInfoList = answers[question.questionID].map(
					answer => ({
						selectedAnswerID: answer,
					})
				);
				break;
			case Actions.QuestionKind.TEXT:
				param.questionKind = 'text';
				const texts = question.answers as Actions.ITextAnswer[];
				param.textAnswerInfoList = texts.map((a, i) => ({
					answerID: a.answerID,
					answerText: answers[question.questionID][i],
				}));
				break;
			case Actions.QuestionKind.CHECK:
				param.questionKind = 'check';
				const checks = question.answers as Actions.ICheckAnswer[];
				param.checkAnswerInfoList = checks.map((a, i) => ({
					answerID: a.answerID,
					selected: answers[question.questionID].find(
						b => b === a.answerID
					)
						? true
						: false,
				}));
				break;
			case Actions.QuestionKind.SELECT:
				param.questionKind = 'select';
				param.selectAnswerInfoList = answers[question.questionID].map(
					answer => ({
						selectedAnswerID: answer,
					})
				);
				break;
		}
		return param;
	}
}



// WEBPACK FOOTER //
// ./src/scenario/TestFormScenarioFactory.ts